<template>
  <IonPageTitleContainer pageTitle="Offline Downloads" pageDescriptor="Manage">
    <template #actionButton>
      <CustomIonButton
        fill="outline"
        :isRound="true"
        color="danger"
        v-if="DownloadCount > 0"
        @onClick="onRemoveAll"
      >
        <i class="ti ti-trash me-1"></i>Remove All
      </CustomIonButton>
    </template>
    <template #pageContent>
      <div v-if="DownloadCount > 0">
        <div class="row-center justify-between flex-wrap gap-x-2 gap-y-2 my-3">
          <div class="row-center-gap-1">
            <i class="ti ti-filter text-s color-tertiary"></i>
            <CustomIonFilterRadioButtons
              inputId="offlineDownloadType"
              :radioItems="typeOptions"
              :initialValue="SelectedType"
              @onChange="onTypeChange"
            />
          </div>
          <div class="row-center-gap-1">
            <i
              class="text-s cursor-pointer"
              :class="
                IsDescendingSort
                  ? 'ti ti-sort-ascending color-tertiary'
                  : 'ti ti-sort-descending color-danger'
              "
              @click="IsDescendingSort = !IsDescendingSort"
            ></i>
            <CustomIonFilterRadioButtons
              inputId="offlineDownloadSort"
              :radioItems="sortOptions"
              :initialValue="SortField"
              @onChange="onSortFieldChange"
            />
          </div>
        </div>
        <CustomIonSearchbar
          placeholder="Search by name..."
          @onSearch="onSearchChange"
          class="offline-search-bar mb-2 max-w-full xxs:max-w-90 w-full min-w-60"
        />
        <div class="row-center justify-between gap-3 mb-1">
          <div class="row-center-gap-2 text-xxs color-tertiary">
            <i class="ti ti-server text-l leading-xs"></i>
            <span class="font-merriweather font-semibold">{{ TotalStorageUsedDisplay }}</span>
          </div>
          <CardsListCounter
            :filteredCount="FilteredDownloads.length"
            :currentCount="DownloadCount"
          />
        </div>
        <div v-if="FilteredDownloads.length > 0">
          <OfflineDownloadCard
            v-for="download in FilteredDownloads"
            :key="download.Id"
            :download="download"
            @onOpen="onOpen"
            @onRemove="onRemove"
          />
        </div>
        <NoRecordFound v-else noRecordsText="No downloads match your search." color="tertiary" />
      </div>
      <NoRecordFound v-else noRecordsText="No offline downloads found." color="tertiary" />
    </template>
  </IonPageTitleContainer>
</template>

<script lang="ts">
import OfflineDownloadCard from "@/components/features/offlineDownload/cards/OfflineDownloadCard.vue";
import CustomIonFilterRadioButtons from "@/components/ionic/CustomIonFilterRadioButtons.vue";
import CustomIonSearchbar from "@/components/ionic/CustomIonSearchbar.vue";
import IonPageTitleContainer from "@/components/layout/IonPageTitleContainer.vue";
import CustomIonButton from "@/components/primitives/CustomIonButton.vue";
import NoRecordFound from "@/components/states/NoRecordFound.vue";
import CardsListCounter from "@/components/widgets/CardsListCounter.vue";
import { OfflineDownloadSortOptionEnum, OfflineDownloadTypeEnum } from "@/enums/offlineDownload";
import { openOfflineDownload } from "@/methods/offlineDownload";
import { getInputOptionsFromEnum, showCustomMessage } from "@/methods/utils";
import { useOfflineDownloadStore } from "@/stores/core/offlineDownloadStore";
import { InputOption } from "@/types/global";
import { OfflineDownloadItem } from "@/types/offlineDownload";
import { onIonViewWillEnter } from "@ionic/vue";
import { storeToRefs } from "pinia";
import { defineComponent } from "vue";

export default defineComponent({
  name: "OfflineDownloads",
  components: {
    CustomIonButton,
    IonPageTitleContainer,
    OfflineDownloadCard,
    NoRecordFound,
    CustomIonSearchbar,
    CustomIonFilterRadioButtons,
    CardsListCounter,
  },
  setup() {
    const offlineDownloadStore = useOfflineDownloadStore();
    const {
      FilteredDownloads,
      DownloadCount,
      TotalStorageUsedDisplay,
      SelectedType,
      SortField,
      IsDescendingSort,
      SearchText,
    } = storeToRefs(offlineDownloadStore);

    const sortOptions: Array<InputOption> = getInputOptionsFromEnum(OfflineDownloadSortOptionEnum);
    const typeOptions: Array<InputOption> = getInputOptionsFromEnum(OfflineDownloadTypeEnum);

    function onTypeChange(value: string) {
      SelectedType.value = value;
    }

    function onSortFieldChange(value: string) {
      if (SortField.value === value) {
        IsDescendingSort.value = !IsDescendingSort.value;
        return;
      }
      SortField.value = value;
    }

    function onSearchChange(value: string) {
      SearchText.value = value;
    }

    onIonViewWillEnter(async () => {
      await offlineDownloadStore.cleanupStaleDownloads();
    });

    async function onOpen(download: OfflineDownloadItem) {
      try {
        await openOfflineDownload(download);
      } catch (error) {
        console.error("Error opening offline download:", error);
        showCustomMessage({ messageText: "Failed to open the file.", messageType: "danger" });
      }
    }

    async function onRemove(downloadId: string) {
      await offlineDownloadStore.removeDownload(downloadId);
      showCustomMessage({ messageText: "Download removed.", messageType: "success" });
    }

    async function onRemoveAll() {
      await offlineDownloadStore.removeAllDownloads();
      showCustomMessage({ messageText: "All downloads removed.", messageType: "success" });
    }

    return {
      DownloadCount,
      TotalStorageUsedDisplay,
      FilteredDownloads,
      typeOptions,
      sortOptions,
      SelectedType,
      SortField,
      IsDescendingSort,
      SearchText,
      onTypeChange,
      onSortFieldChange,
      onSearchChange,
      onOpen,
      onRemove,
      onRemoveAll,
    };
  },
});
</script>
